/**
 * ItemRef — the one way to name an item.
 *
 * Before 5D every layer named items its own way: pickups carried `abilityId`
 * or `weaponId` side by side, loot tables named pickup ids (`weapon_shotgun`),
 * inventory slots carried a bare `itemId` that was assumed to be a weapon.
 * Adding a kind meant teaching every one of those layers about it.
 *
 * An **item ref** is `{ kind, id }` — `kind` drawn from a closed set, `id` the
 * item's own id inside its registry — with a canonical string form `kind:id`
 * (`weapon:shotgun`, `ability:firewall`, `consumable:small_shield_potion`).
 *
 * `parseRef` is the funnel: every legacy spelling still in the tree (Tiled maps
 * emit `ability_teleport`, older payloads carry `{ abilityId }`) resolves
 * through it, so nothing had to be migrated on the day this landed.
 *
 * Pure ESM with no imports — shared/, server and client all reach for it.
 */

/** The closed set of item kinds. Adding one here is the first half of adding a kind. */
const KINDS = ['weapon', 'ability', 'consumable', 'ammo'];

/** Legacy `{ <kind>Id: 'x' }` spellings, in the order they are checked. */
const LEGACY_ID_FIELDS = [
  ['weaponId', 'weapon'],
  ['abilityId', 'ability'],
  ['consumableId', 'consumable']
];

const isKind = (kind) => typeof kind === 'string' && KINDS.includes(kind);

/** A non-empty, non-blank item id. */
function cleanId(id) {
  if (typeof id !== 'string') return null;
  const trimmed = id.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function makeRef(kind, id) {
  const cleanedId = cleanId(id);
  return isKind(kind) && cleanedId ? { kind, id: cleanedId } : null;
}

/**
 * True only for a well-formed `{ kind, id }` with a known kind. Strings and
 * legacy shapes are *parseable* but are not themselves refs — callers that need
 * "can this be turned into a ref" want `parseRef(x) !== null` instead.
 * @param {*} value
 * @returns {boolean}
 */
function isRef(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    isKind(value.kind) &&
    cleanId(value.id)
  );
}

/**
 * Resolve any spelling of an item name into `{ kind, id }`.
 *
 * Accepts: `'kind:id'`, `{ kind, id }` (extra fields ignored), `{ ref }`,
 * legacy `{ weaponId }` / `{ abilityId }` / `{ consumableId }`, and — only when
 * `legacyPickupLookup` is supplied — a bare legacy pickup id such as
 * `'weapon_shotgun'`. Bare ids cannot be split on `'_'` because item ids
 * contain underscores themselves (`assault_rifle`), so they are resolved
 * through the pickup table rather than guessed at.
 *
 * @param {string|object|null|undefined} input
 * @param {{ legacyPickupLookup?: (id: string) => (object|string|null) }} [options]
 * @returns {{ kind: string, id: string }|null} a fresh object, or null if unresolvable
 */
function parseRef(input, options = {}) {
  return parseRefInternal(input, options, 0);
}

const MAX_PARSE_DEPTH = 4;   // { ref: { ref: … } } and lookup indirection are bounded

function parseRefInternal(input, options, depth) {
  if (input === null || input === undefined || depth > MAX_PARSE_DEPTH) return null;

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;

    const separator = trimmed.indexOf(':');
    if (separator !== -1) {
      return makeRef(trimmed.slice(0, separator), trimmed.slice(separator + 1));
    }

    // A bare id: only a caller that owns a pickup table can say what it means.
    const lookup = options.legacyPickupLookup;
    if (typeof lookup !== 'function') return null;
    const resolved = lookup(trimmed);
    return resolved ? parseRefInternal(resolved, options, depth + 1) : null;
  }

  if (typeof input !== 'object' || Array.isArray(input)) return null;

  if (input.ref !== undefined && input.ref !== null) {
    return parseRefInternal(input.ref, options, depth + 1);
  }

  // Legacy id fields win over a bare `id`: a payload carrying both
  // `{ weaponId: 'shotgun', id: 'weapon_shotgun' }` means the weapon.
  for (const [field, kind] of LEGACY_ID_FIELDS) {
    if (input[field]) {
      const ref = makeRef(kind, input[field]);
      if (ref) return ref;
    }
  }

  return makeRef(input.kind, input.id);
}

/**
 * The canonical `'kind:id'` string — the key to use in Maps, payloads and logs.
 * Accepts anything `parseRef` accepts so callers need not pre-parse.
 * @param {string|object} refOrInput
 * @param {object} [options] forwarded to parseRef
 * @returns {string|null}
 */
function refKey(refOrInput, options = {}) {
  const ref = isRef(refOrInput) ? refOrInput : parseRef(refOrInput, options);
  return ref ? `${ref.kind}:${ref.id}` : null;
}

/**
 * The ref for a live item instance.
 *
 * `Item.type` already spells the kind for the kinds that are Items (weapons,
 * consumables). Abilities are not Items — their holder passes `kind` in.
 *
 * @param {{ id: string, type?: string, kind?: string }} item
 * @param {string} [kind] overrides the item's own type
 * @returns {{ kind: string, id: string }|null}
 */
function refForItem(item, kind = null) {
  if (!item || typeof item !== 'object') return null;
  return makeRef(kind || item.kind || item.type, item.id);
}

/**
 * True when two refs name the same item. Either side may be any parseable form.
 * @returns {boolean}
 */
function refsEqual(a, b) {
  const keyA = refKey(a);
  return keyA !== null && keyA === refKey(b);
}

export { parseRef, refKey, isRef, refsEqual, refForItem, KINDS };
